import { useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MoveUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useCollections } from '@/hooks/useCollections';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'faucets', label: 'Faucets' },
  { id: 'kitchen', label: 'Kitchen' },
  { id: 'showers', label: 'Showers' },
  { id: 'accessories', label: 'Accessories' },
];

const TiltCard = ({ c, index, onOpen, onInquire }) => {
  const ref = useRef(null);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    setTilt({ rx: -py * 7, ry: px * 7 });
  };

  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      className="group"
      style={{ perspective: 900 }}
      data-testid={`collection-card-${c.id}`}
    >
      <div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={() => setTilt({ rx: 0, ry: 0 })}
        onClick={() => onOpen(c)}
        className="relative bg-[#121215] border border-[#D4AF37]/15 overflow-hidden transition-colors duration-500 group-hover:border-[#D4AF37]/50 cursor-pointer"
        style={{
          transform: `rotateX(${tilt.rx}deg) rotateY(${tilt.ry}deg)`,
          transition: 'transform 0.25s ease-out',
          transformStyle: 'preserve-3d',
        }}
      >
        <div className={`relative h-64 overflow-hidden ${c.image?.includes('/img/jsp-') ? 'bg-black' : ''}`}>
          <img
            src={c.image}
            alt={`${c.name} collection`}
            loading="lazy"
            className={`w-full h-full transition-transform duration-700 ease-out group-hover:scale-110 ${c.image?.includes('/img/jsp-') ? 'object-contain' : 'object-cover'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#121215] via-transparent to-transparent" />
          <span className="absolute top-4 left-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#0A0A0B] bg-[#D4AF37] px-2.5 py-1">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="absolute bottom-4 right-4 glass-dark px-3.5 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#F5E6AD] opacity-0 group-hover:opacity-100 transition-opacity duration-400">
            View Collection
          </span>
        </div>
        <div className="p-6">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]/80 mb-2">{c.line}</p>
          <h3 className="font-serif text-2xl text-[#F5F4F0] group-hover:text-[#F5E6AD] transition-colors">{c.name}</h3>
          <p className="mt-2 text-sm text-zinc-400 font-light leading-relaxed line-clamp-2">{c.blurb}</p>
          {c.items?.length > 0 && (
            <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500" data-testid={`collection-count-${c.id}`}>
              {c.items.length} products
            </p>
          )}
          <div className="mt-4 flex items-center justify-between">
            <button
              data-testid={`collection-inquire-${c.id}`}
              onClick={(e) => {
                e.stopPropagation();
                onInquire({ collection: c.name, model: c.featured?.model, product: c.featured?.name });
              }}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-[#F5E6AD] transition-colors"
            >
              Inquire Now
            </button>
            <MoveUpRight size={16} className="text-zinc-600 group-hover:text-[#D4AF37] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all duration-300" />
          </div>
        </div>
      </div>
    </motion.article>
  );
};

export const Collections = () => {
  const collections = useCollections();
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');

  const shown = filter === 'all' ? collections : collections.filter((c) => c.category === filter);

  const inquire = (prefill) => navigate('/contact', { state: { prefill } });

  return (
    <section id="collections" data-testid="product-catalogue" className="relative py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-14">
          <div>
            <p className="kicker mb-4">The Catalogue — April 2026</p>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight font-light">
              {shown.length} Collections,<br />
              <span className="italic text-gold-gradient">one obsession.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="catalogue-filters">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                data-testid={`filter-tab-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors duration-300 ${
                  filter === f.id
                    ? 'bg-[#D4AF37] text-[#0A0A0B] border-[#D4AF37]'
                    : 'border-zinc-800 text-zinc-400 hover:border-[#D4AF37]/60 hover:text-[#F5E6AD]'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          <AnimatePresence mode="popLayout">
            {shown.map((c, i) => (
              <TiltCard
                key={c.id}
                c={c}
                index={i}
                onOpen={(col) => navigate(`/collections/${col.id}`)}
                onInquire={inquire}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};
